import { ReactNode, useEffect, useRef, useState } from 'react';
import { Accordion } from '@/shared/components/ui/accordion';
import { OnboardingTask, OnboardingTaskKey, OnboardingTaskStatus } from '@/types/api/group/onboarding-task';
import { useSelectBankAccount } from '@/features/payout-accounts/hooks/use-select-bank-account';
import { OnboardingTaskCard } from './onboarding-task-card';
import { InviteMembersTask } from './invite-members-task';
import { PayoutTask } from './payout-task';
import { CreateSaleTask } from './create-sale-task';

const isCompleted = (task: OnboardingTask) => task.status === OnboardingTaskStatus.Completed;

/** The inline content of a task, or nothing when the task can't be expanded */
function taskContent(task: OnboardingTask, groupId: string): ReactNode {
  if (task.status === OnboardingTaskStatus.Disabled) return null;

  // prettier-ignore
  switch (task.key) {
    // Inviting more deltagare still makes sense after the task is done
    case OnboardingTaskKey.InviteMembers:       return <InviteMembersTask groupId={groupId} />;
    case OnboardingTaskKey.CreatePayoutAccount: return isCompleted(task) ? null : <PayoutTask groupId={groupId} />;
    case OnboardingTaskKey.CreateSale:          return isCompleted(task) ? null : <CreateSaleTask groupId={groupId} />;
    default:                                    return null;
  }
}

export function OnboardingTaskList({ tasks, groupId }: { tasks: OnboardingTask[]; groupId: string }) {
  const sorted = [...tasks].sort((a, b) => a.order - b.order);
  const firstPending = sorted.find((task) => task.status === OnboardingTaskStatus.Pending)?.key ?? '';

  // Coming back from the bank's account selection lands on this page
  const { id: bankSelectionId, errorCode: bankSelectionError } = useSelectBankAccount();
  const [open, setOpen] = useState<string>(
    bankSelectionId || bankSelectionError ? OnboardingTaskKey.CreatePayoutAccount : firstPending,
  );

  // When a task gets completed (the group is polled), move on to the next one
  const completedKeys = sorted.filter(isCompleted).map((task) => task.key).join();
  const previousCompletedKeys = useRef(completedKeys);

  useEffect(() => {
    if (previousCompletedKeys.current === completedKeys) return;
    previousCompletedKeys.current = completedKeys;
    setOpen(firstPending);
  }, [completedKeys, firstPending]);

  return (
    <Accordion type="single" collapsible value={open} onValueChange={setOpen} className="space-y-3">
      {sorted.map((task) => (
        <OnboardingTaskCard
          key={task.key}
          task={task}
          // Completing the task opens the next one, which mustn't close the invite dialog
          keepMounted={task.key === OnboardingTaskKey.InviteMembers}
        >
          {taskContent(task, groupId)}
        </OnboardingTaskCard>
      ))}
    </Accordion>
  );
}
